import { Badge, Stack, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { AutoDataTable } from "../../../components/AutoDataTable";
import { ListPageHeader } from "../../../components/PageHeader";
import { DELETE_PRESET_SCRIPT_MUTATION } from "../steps/rules/graphql/mutations";
import { PRESET_SCRIPTS_QUERY } from "../steps/rules/graphql/queries";
import { PresetParameterDraft } from "./PresetParametersEditor";

const PRESET_SCRIPTS_BASE_PATH = "/tournaments/preset-scripts";

interface PresetScriptsListProps {
  readonly showHeader?: boolean;
}

interface PresetScriptListItem {
  id: string;
  name: string;
  description: string | null;
  script: string;
  parameters: PresetParameterDraft[] | null;
  createdAt: string;
  updatedAt: string;
}

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

export const PresetScriptsList = ({
  showHeader = false,
}: PresetScriptsListProps) => {
  const navigate = useNavigate();

  return (
    <Stack gap="md">
      {showHeader && (
        <ListPageHeader
          title="Preset Scripts"
          createLabel="Create Preset"
          onCreate={() => navigate("/tournaments/preset-scripts/new")}
        />
      )}
      <AutoDataTable
        itemPath={PRESET_SCRIPTS_BASE_PATH}
        createButtonText="Create Preset"
        query={PRESET_SCRIPTS_QUERY}
        deleteMutation={DELETE_PRESET_SCRIPT_MUTATION}
        columns={[
          {
            accessor: "name",
            title: "Name",
          },
          {
            accessor: "description",
            title: "Description",
            rawElement: (preset: PresetScriptListItem) => (
              <Text
                size="sm"
                c={preset.description ? undefined : "dimmed"}
                lineClamp={2}
              >
                {preset.description || "No description"}
              </Text>
            ),
          },
          {
            accessor: "parameters",
            title: "Parameters",
            description: "Number of declared parameters",
            rawElement: (preset: PresetScriptListItem) => (
              <Badge variant="light" color="cyan">
                {preset.parameters?.length ?? 0}
              </Badge>
            ),
          },
          {
            accessor: "createdAt",
            title: "Created",
            description: "When this preset was first created",
            rawElement: (preset: PresetScriptListItem) => (
              <>{formatDate(preset.createdAt)}</>
            ),
          },
        ]}
      />
    </Stack>
  );
};
